/**
 * PaperPreviewList – shared scrollable paper-card list for bulk download UIs.
 *
 * Handles:
 * - Optional loading spinner
 * - Scrollable list of WorkCards (with optional "included" source work)
 * - Custom empty-state slot
 * - Caller-provided `footer` overlay (absolute-positioned at the bottom)
 *
 */

import React from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';

import { BundleWork } from './types';
import { WorkCard } from './WorkCard';

export interface PaperPreviewListProps {
  /** Related works to display (excluding the source/included work). */
  works: BundleWork[];
  /** Optional source/included work shown first with a header. */
  includedWork?: BundleWork;
  /** Header text for the included work card. */
  includedWorkHeader?: string;
  /** Show a loading spinner instead of the list. */
  loading?: boolean;
  /** Text shown below the spinner. */
  loadingMessage?: string;
  /** Custom empty-state content rendered when there are no works. */
  emptyState?: React.ReactNode;
  /**
   * Overlay rendered at the bottom of the list (absolute-positioned).
   * Use `<PaperPreviewList.FadeFooter>` for the blue-gradient "+N papers"
   * treatment, or pass any custom element (e.g. a white gradient overlay).
   */
  footer?: React.ReactNode;
  /** Show admin-only debug annotations (bucket, similarity score). */
  showAnnotations?: boolean;
  /** Show relevancy badges on cards (default true). */
  showRelevancy?: boolean;
  /** Where to render the relevancy badge on each card. */
  badgePlacement?: 'side' | 'below' | 'above';
  /** CSS max-height for the scrollable area. */
  maxHeight?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function PaperPreviewList({
  works,
  includedWork,
  includedWorkHeader = 'The paper you are reading (included)',
  loading = false,
  loadingMessage = 'Preparing your PDF pack\u2026',
  emptyState,
  footer,
  showAnnotations = false,
  showRelevancy = true,
  badgePlacement,
  maxHeight,
  className,
  style,
}: PaperPreviewListProps) {
  const hasContent = works.length > 0 || !!includedWork;

  const containerStyle: React.CSSProperties = {
    ...style,
    ...(maxHeight ? { maxHeight } : {}),
  };

  return (
    <ListContainer className={className} style={containerStyle}>
      {loading ? (
        <LoadingState>
          <Spinner aria-hidden />
          <Body size="md" as="div" style={primaryText}>
            {loadingMessage}
          </Body>
        </LoadingState>
      ) : !hasContent && emptyState ? (
        <>{emptyState}</>
      ) : (
        <>
          <ScrollArea>
            {includedWork && (
              <WorkCard
                work={includedWork}
                header={includedWorkHeader}
                showRelevancy={false}
                showAnnotations={showAnnotations}
                badgePlacement={badgePlacement}
              />
            )}

            {works.map((work) => (
              <WorkCard
                key={work.id}
                work={work}
                showRelevancy={showRelevancy}
                showAnnotations={showAnnotations}
                badgePlacement={badgePlacement}
              />
            ))}
          </ScrollArea>

          {footer}
        </>
      )}
    </ListContainer>
  );
}

// ---------------------------------------------------------------------------
// Pre-built footer variants – importable by consumers
// ---------------------------------------------------------------------------

/** Blue-gradient fade footer with "+N related research papers" label. */
export function FadeFooter({
  hiddenPapersCount,
}: {
  hiddenPapersCount: number;
}) {
  if (hiddenPapersCount <= 0) return null;
  return (
    <BlueFadeFooter>
      <FadeFooterInner>
        <Body size="lg" bold as="strong">
          +{hiddenPapersCount}
        </Body>
        <Body size="xs" as="span">
          related research papers
        </Body>
      </FadeFooterInner>
    </BlueFadeFooter>
  );
}

/** Simple white-to-transparent gradient overlay (no text). */
export function GradientOverlay() {
  return <WhiteGradient />;
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const primaryText: React.CSSProperties = {
  color: 'var(--text-primary, #141413)',
};

const ListContainer = styled.div`
  position: relative;
  overflow-y: auto;
`;

const ScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-right: 6px;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  padding: var(--spacing-sm-24, 24px);
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 999px;
  border: 4px solid var();
  border-top-color: var(--primary-brand, #0645b1);
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const BlueFadeFooter = styled.div`
  position: sticky;
  left: 0;
  right: 0;
  bottom: 0;
  height: 84px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: var(--spacing-xs-16, 16px);
  background: linear-gradient(
    180deg,
    rgba(244, 247, 252, 0) 5.8%,
    rgba(244, 247, 252, 0.5) 13.34%,
    rgba(244, 247, 252, 0.74) 22.1%,
    rgba(244, 247, 252, 0.77) 28.48%,
    #f4f7fc 39.01%
  );
  pointer-events: none;
`;

const FadeFooterInner = styled.div`
  display: inline-flex;
  gap: var(--spacing-xs-4, 4px);
  align-items: baseline;
  color: var(--text-primary, #141413);
`;

const WhiteGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 1) 90%
  );
  pointer-events: none;
`;

export default PaperPreviewList;
